import React, { useState } from 'react';

import { FilterPopover } from './FilterPopover';
import { StatusBadge } from './StatusBadge';

export function BatchesSidebar({
  isOpen,
  onToggle,
  projects,
  filters,
  pendingFilters,
  showVoid,
  loading,
  batches,
  visibleBatches,
  selectedBatchId,
  onSearchChange,
  onToggleVoid,
  onSelectBatch,
  onQuickFinalize,
  onStatusToggle,
  onProjectChange,
  onDateChange,
  onApplyFilters,
  onClearFilters,
  onSyncPendingFilters,
}) {
  const [showFilterPopover, setShowFilterPopover] = useState(false);
  const renderedBatches =
    visibleBatches || batches.filter((batch) => showVoid || batch.status !== 'void');

  const openFilters = () => {
    onSyncPendingFilters();
    setShowFilterPopover(true);
  };

  const closeFilters = () => setShowFilterPopover(false);

  const applyFilters = () => {
    onApplyFilters();
    closeFilters();
  };

  const clearFilters = () => {
    onClearFilters();
    closeFilters();
  };

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 border-b px-3 py-3">
        <div className="flex items-center justify-between gap-2">
          {isOpen && (
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Batches
            </h2>
          )}

          <button
            type="button"
            role="switch"
            aria-checked={isOpen}
            aria-label={isOpen ? 'Hide batches sidebar' : 'Show batches sidebar'}
            onClick={onToggle}
            className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
              isOpen ? 'bg-indigo-600' : 'bg-slate-300'
            }`}
          >
            <span
              aria-hidden="true"
              className={`absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                isOpen ? 'translate-x-4' : 'translate-x-0'
              }`}
            />
          </button>
        </div>

        {isOpen && (
          <div className="mt-3 space-y-2 text-sm">
            <label className="block">
              <input
                className="w-full rounded border border-slate-300 p-2 text-sm"
                placeholder="Search batches…"
                value={filters.search}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </label>

            <div className="flex items-center justify-between gap-2">
              <button
                className="rounded border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                type="button"
                onClick={openFilters}
              >
                Filter ▾
              </button>

              <label className="flex items-center gap-2 text-xs font-semibold text-slate-500">
                <input
                  type="checkbox"
                  checked={showVoid}
                  onChange={(e) => onToggleVoid(e.target.checked)}
                />
                Show void
              </label>
            </div>

            {showFilterPopover && (
              <FilterPopover
                projects={projects}
                pendingFilters={pendingFilters}
                onStatusToggle={onStatusToggle}
                onProjectChange={onProjectChange}
                onDateChange={onDateChange}
                onApply={applyFilters}
                onClear={clearFilters}
                onClose={closeFilters}
              />
            )}
          </div>
        )}
      </div>

      {isOpen && (
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto px-4 py-3">
          {loading && <p className="text-sm text-slate-500">Loading…</p>}

          {!loading && renderedBatches.length === 0 && (
            <p className="text-sm text-slate-500">No batches yet.</p>
          )}

          {renderedBatches.map((batch) => (
            <div
              key={batch.id}
              className={`rounded border px-3 py-2 ${
                batch.id === selectedBatchId
                  ? 'border-slate-900 bg-slate-50'
                  : 'border-slate-200 hover:bg-slate-50'
              }`}
              onClick={() => onSelectBatch(batch.id)}
            >
              <div className="flex items-center justify-between text-sm">
                <div>
                  <div className="flex items-center gap-1">
                    <p className="font-semibold text-slate-900">{batch.code}</p>
                    <StatusBadge status={batch.status} />
                  </div>

                  <p className="text-xs text-slate-500">
                    {batch.project_detail?.code ||
                      batch.project_detail?.name ||
                      '—'}
                  </p>
                </div>
              </div>

              <p className="text-xs text-slate-500">
                {batch.sample_count ?? 0} sample
                {(batch.sample_count ?? 0) === 1 ? '' : 's'}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
